// ============================================================================
// SPOTLIGHT OVERLAY - Full-screen display of a spotlighted contribution
// Triggered by facilitator from the main window's contributions panel
// ============================================================================

import React, { useEffect, useState } from 'react';
import { useSecondScreenData } from '../../contexts/SecondScreenContext';

const SpotlightOverlay: React.FC = () => {
  const { activeSpotlight, clearSpotlight, state } = useSecondScreenData();
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (activeSpotlight) {
      // Trigger entrance animation
      requestAnimationFrame(() => setAnimateIn(true));
    } else {
      setAnimateIn(false);
    }
  }, [activeSpotlight]);

  // Escape key to dismiss
  useEffect(() => {
    if (!activeSpotlight) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearSpotlight();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [activeSpotlight, clearSpotlight]);

  if (!activeSpotlight) return null;

  // Find avatar info
  const avatar = state?.sessionAvatars.find(a => a.role === activeSpotlight.role);
  const urgencyColors: Record<string, string> = {
    urgent: 'bg-red-500 text-white',
    important: 'bg-amber-500 text-white',
    normal: 'bg-primary/20 text-primary',
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-12 cursor-pointer"
      onClick={clearSpotlight}
    >
      <div
        className={`max-w-4xl w-full transition-all duration-500 ${
          animateIn ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Avatar header */}
        <div className="flex items-center gap-6 mb-8">
          {avatar?.imageUrl ? (
            <img
              src={avatar.imageUrl}
              alt={avatar.name}
              className="w-24 h-24 rounded-full border-4 border-primary/50 object-cover"
            />
          ) : (
            <div className="w-24 h-24 rounded-full border-4 border-primary/50 bg-white/10 flex items-center justify-center text-4xl">
              {avatar?.emoji || '?'}
            </div>
          )}
          <div>
            <h2 className="text-3xl font-bold text-white">{avatar?.name || activeSpotlight.name}</h2>
            <p className="text-lg text-white/50 capitalize">{activeSpotlight.role.replace(/_/g, ' ')}</p>
            <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${urgencyColors[activeSpotlight.urgency] || urgencyColors.normal}`}>
              {activeSpotlight.urgency}
            </span>
          </div>
        </div>

        {/* Contribution text */}
        <div className="bg-white/5 rounded-2xl border border-white/10 p-8">
          <p className="text-2xl leading-relaxed text-white/90">{activeSpotlight.response}</p>
        </div>

        {/* Dismiss hint */}
        <p className="text-center mt-6 text-white/30 text-sm">Click anywhere or press Escape to dismiss</p>
      </div>
    </div>
  );
};

export default SpotlightOverlay;
